import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { getUserDataConfig } from "@/utils/helpers/configs"
import { RootState } from "@/store"
import { InvestmentData } from "@/types/Investment"
import { AccountBalance, InvestmentOption } from "@/types/Product"
import { Message, PendingTypeDefinition } from "@/types/General"
import { MemberDocument } from "@/types/LinkApi"
import { SortingOptions, SortOrderProps } from "@components/Inbox/InboxControls"

const matrixData = getUserDataConfig()

const searchParams = new URLSearchParams(window.location.search)

const docsRead = matrixData?.metadata?.documentsRead?.value || []
const messagesRead = matrixData?.metadata?.messagesRead?.value || []

interface UiState {
  isMenuOpen: boolean
  currentAccountIndex: number
  investmentsOpen: InvestmentData["unitNumber"][] | null
  performanceOpen: InvestmentData["unitNumber"][] | null
  balanceDashOpen: AccountBalance["uid"][] | null
  transactionPanel: PendingTypeDefinition[]
  inboxSortAndFilters: Partial<
    Record<SortOrderProps["item"], { sortOrder: SortingOptions }>
  >
  documentsRead: MemberDocument["documentId"][]
  messagesRead: Message["assetId"][]
  feesOpen: InvestmentOption["uid"][]
  /**
   * Optional document title which will override the default
   * title behaviour with uses the location and is set in App
   */
  title: string | null
}

const initialState: UiState = {
  isMenuOpen: false,
  // the currentAccountIndex is the index of the account within the
  // accountBalances query which is sorted and includes logic to
  // determine TTR accounts, which couldn't be included users memberships
  currentAccountIndex: +(searchParams.get("current") ?? 0),
  investmentsOpen: null,
  performanceOpen: null,
  balanceDashOpen: null,
  transactionPanel: [],
  inboxSortAndFilters: {},
  documentsRead:
    typeof docsRead === "string" ? JSON.parse(docsRead) : [...docsRead],
  messagesRead:
    typeof messagesRead === "string"
      ? JSON.parse(messagesRead)
      : [...messagesRead],
  feesOpen: [],
  title: null,
}

// Updates/inserts the "current" param in a raw query string without touching any other
// param's raw form — round-tripping the whole string through URLSearchParams.set()+toString()
// normalises bare flags like "disableMfa" into "disableMfa=", corrupting them.
function withCurrentParam(search: string, value: number): string {
  const raw = search.replace(/^\?/, "")
  const parts = raw.length ? raw.split("&") : []
  let found = false
  const updated = parts.map((part) => {
    if (part === "current" || part.startsWith("current=")) {
      found = true
      return `current=${value}`
    }
    return part
  })
  if (!found) {
    updated.push(`current=${value}`)
  }
  return updated.length ? `?${updated.join("&")}` : ""
}

// only possible due to RTK using Immer
function toggleArrayItem(array: string[] | null, key: string) {
  if (array) {
    const index = array.indexOf(key)
    index > -1 ? array.splice(index, 1) : array.push(key)
  } else {
    console.error("Invalid request to toggle array item with array not set")
  }
}

export const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    toggleMenuOpen: (state) => {
      state.isMenuOpen = !state.isMenuOpen
    },
    forceMenuClosed: (state) => {
      state.isMenuOpen = false
    },
    setAccountIndex: (state, action) => {
      state.currentAccountIndex = action.payload
      // update the url to include the current index so users can copy/paste the url
      // we're not actually using this for navigation so kept outside router logic
      const url = new URL(window.location.href)
      // manual string surgery (not url.searchParams.set) preserves bare flags like
      // ?disableMfa untouched instead of rewriting them to ?disableMfa=
      url.search = withCurrentParam(url.search, action.payload)
      window.history.replaceState(null, "", url)
    },
    toggleInvestmentOpen: (
      state,
      { payload }: PayloadAction<InvestmentData>
    ) => {
      toggleArrayItem(state.investmentsOpen, payload.unitNumber)
    },
    setInvestmentsOpen: (state, action: PayloadAction<InvestmentData[]>) => {
      state.investmentsOpen =
        action.payload?.map((item) => item.unitNumber) ?? null
    },
    setPerformanceOpen: (state, { payload }) => {
      state.performanceOpen = payload
    },
    togglePerformanceOpen: (state, { payload }) => {
      toggleArrayItem(state.performanceOpen, payload)
    },
    setBalanceDashOpen: (state, { payload }) => {
      state.balanceDashOpen = [payload]
    },
    toggleBalanceDashOpen: (state, { payload }) => {
      toggleArrayItem(state.balanceDashOpen, payload)
    },
    resetInitialState(state) {
      Object.assign(state, initialState)
    },
    setTransactionsState(state, { payload }) {
      const { id } = payload
      const existing = state.transactionPanel.find((item) => item.id === id)
      if (existing) {
        state.transactionPanel.splice(
          state.transactionPanel.indexOf(existing),
          1,
          payload
        )
      } else {
        state.transactionPanel.push(payload)
      }
    },
    setInboxSortAndFilters(state, { payload }) {
      state.inboxSortAndFilters = { ...state.inboxSortAndFilters, ...payload }
    },
    setDocumentsReadState(state, { payload }) {
      state.documentsRead = [...payload]
    },
    setMessagesReadState(state, { payload }) {
      state.messagesRead = [...payload]
    },
    toggleFeesOpen(state, { payload }) {
      toggleArrayItem(state.feesOpen, payload)
    },
    setTitle(state, { payload }) {
      state.title = payload
    },
  },
})

// Action creators are generated for each case reducer function
export const {
  toggleMenuOpen,
  setInvestmentsOpen,
  toggleInvestmentOpen,
  setBalanceDashOpen,
  toggleBalanceDashOpen,
  setAccountIndex,
  resetInitialState,
  togglePerformanceOpen,
  setTransactionsState,
  setInboxSortAndFilters,
  setDocumentsReadState,
  setMessagesReadState,
  toggleFeesOpen,
  setPerformanceOpen,
  setTitle,
} = uiSlice.actions

export const selectInvestmentsOpen = (state: RootState) =>
  state.ui.investmentsOpen
export const selectBalanceDashOpen = (state: RootState) =>
  state.ui.balanceDashOpen
export const selectCurrentAccountIndex = (state: RootState) =>
  state.ui.currentAccountIndex
export const selectIsMenuOpen = (state: RootState) => state.ui.isMenuOpen
export const selectPerformanceOpen = (state: RootState) =>
  state.ui.performanceOpen

export const selectTransactionPanel = (state: RootState) =>
  state.ui.transactionPanel
export const selectTransactionPanelById = createSelector(
  [selectTransactionPanel, (state: RootState, id) => id],
  (items, id) => items.find((item) => item.id === id)
)

export const selectInboxSortAndFilters = (state: RootState) =>
  state.ui.inboxSortAndFilters
export const selectDocumentsRead = (state: RootState) => state.ui.documentsRead
export const selectMessagesRead = (state: RootState) => state.ui.messagesRead

export const selectExpandedFees = (state: RootState, id: string) =>
  state.ui.feesOpen.includes(id)

export const selectTitle = (state: RootState) => state.ui.title

export default uiSlice.reducer
